import { lazy, Suspense, useEffect } from "react";
import {
  Navigate,
  Outlet,
  useNavigate,
  useLocation,
  isRouteErrorResponse,
  useRouteError,
} from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { SnackbarProvider, closeSnackbar } from "notistack";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/de";
import localeData from "dayjs/plugin/localeData";

//* Contexts
import { AttendanceProvider } from "../contexts/AttendanceContext";
import { UserProvider, useUserContext } from "../contexts/UserContext";
import {
  TranslationsProvider,
  useTranslationsContext,
} from "../contexts/TranslationsContext";

//* Constants
import { ROUTES } from "../constants/routes";

//* Components
import AppDrawer from "../layout/app-drawer/AppDrawer";
import DemoDrawerLazy from "../modules/demo/components/DemoDrawer";
import { DemoDataProvider } from "../modules/demo/context/DemoDataContext";
import Breadcrumbs from "../layout/breadcrumbs/breadcrumbs";
import AddHomeScreenSlider from "../components/add-to-home-screen/AddToHomeScreen";
import ErrorBoundary from "../components/error-boundary/ErrorBoundary";

//* Types
import { Languages } from "../types";

//* Hooks
import useAddToHomescreenPrompt from "../hooks/UseAddToHomeScreen";

//* Helpers
import { isMobileDevice } from "../helpers";

//* Init dayjs localization
dayjs.extend(localeData);

//* Lazy-loaded pages
const Home = lazy(() => import("./home/Home"));
const Login = lazy(() => import("./login/Login"));
const PageNotFound = lazy(() => import("./page-not-found/PageNotFound"));
const ClassAttendance = lazy(() => import("../modules/particify/pages/class-attendance/ClassAttendance"));
const StudentAttendance = lazy(() => import("../modules/particify/pages/student-attendance/StudentAttendance"));
const SignUp = lazy(() => import("./sign-up/SignUp"));
const ForgotPassword = lazy(() => import("./forgot-password/ForgotPassword"));
const UpdatePassword = lazy(() => import("./update-password/UpdatePassword"));
// TrackifyHome removed — hub is now unified at "/"
const TrackifyClassView = lazy(() => import("../modules/trackify/pages/TrackifyClassView"));
const ClassDetail = lazy(() => import("../modules/particify/pages/class-detail/ClassDetail"));
const CalendFyPage = lazy(() => import("../modules/calendfy/pages/CalendFyPage"));
const NotifySettingsPage = lazy(() => import("../modules/notify/pages/NotifySettingsPage"));
const LogiFyPage = lazy(() => import("../modules/logify/pages/LogiFyPage"));
const ReportifyPage = lazy(() => import("../modules/reportify/pages/ReportifyPage"));
const ReportDetailPage = lazy(() => import("../modules/reportify/pages/ReportDetailPage"));
const DemoClassAttendance = lazy(() => import("../modules/demo/pages/DemoClassAttendance"));
const DemoStudentTimeline = lazy(() => import("../modules/demo/pages/DemoStudentTimeline"));

//* Suspense fallback
function PageLoader() {
  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
      <div className="loading-spinner" />
    </div>
  );
}

//* Init app theme
const theme = createTheme({
  components: {
    MuiTooltip: {
      defaultProps: {
        enterTouchDelay: 10,
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: "#262240",
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 640,
      md: 768,
      lg: 1024,
      xl: 1536,
    },
  },
  palette: {
    mode: "dark",
    primary: {
      main: "#7aacb3",
    },
    secondary: {
      main: "#CF8DA8",
    },
    background: {
      default: "#3b3758",
      paper: "#3b3758",
    },
  },
});

function RouteErrorBoundary() {
  const error = useRouteError();
  console.log("RouteErrorBoundary: ", error);
  if (isRouteErrorResponse(error)) {
    return (
      <h1>
        {error.status} {error.statusText}
      </h1>
    );
  }
  const message = error instanceof Error ? error.message : String(error);
  return <h1>{message}</h1>;
}

//* Component app Layout
function AppLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const userContext = useUserContext();
  const { language } = useTranslationsContext();
  const [prompt, promptToInstall, installed] = useAddToHomescreenPrompt();

  const isLoginOrSignUp =
    location.pathname === ROUTES.LOG_IN || location.pathname === ROUTES.SIGN_UP;
  const isForgotOrUpdatePassword =
    location.pathname.includes(ROUTES.UPDATE_PASSWORD.split(":")[0]) ||
    location.pathname === ROUTES.FORGOT_PASSWORD;
  const isDemo = location.pathname.startsWith("/demo");

  const isLoginPages = isLoginOrSignUp || isForgotOrUpdatePassword;

  function handleAddHomeScreenSubmit() {
    userContext.setAppInstalledViewed(true);
  }

  //* Redirect user to login page when have no credentials
  useEffect(() => {
    if (!userContext.token && !isLoginOrSignUp && !isForgotOrUpdatePassword && !isDemo) {
      navigate(ROUTES.LOG_IN, { replace: true });
    }
  }, [isLoginOrSignUp, isForgotOrUpdatePassword, isDemo, navigate, userContext.token]);

  //* Change the dayjs locale after language app change
  useEffect(() => {
    if (language === Languages.DEUTSCH) {
      dayjs.locale("de");
    } else {
      dayjs.locale("en");
    }
  }, [language]);

  return (
    <ThemeProvider theme={theme}>
      {!isLoginOrSignUp && !isForgotOrUpdatePassword && !isDemo && (
        <AppDrawer />
      )}
      <div className="outlet-app" data-is-login={isLoginPages}>
        {!isLoginPages && !isDemo && <Breadcrumbs />}
        <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            {isDemo ? (
              <DemoDataProvider>
                <DemoDrawerLazy />
                <Outlet />
              </DemoDataProvider>
            ) : (
              <Outlet />
            )}
          </Suspense>
        </ErrorBoundary>
        {isMobileDevice() &&
          !userContext.appInstalledViewed &&
          !isLoginPages && (
            <AddHomeScreenSlider
              open={!userContext.appInstalledViewed}
              onClose={handleAddHomeScreenSubmit}
              onHandleSubmit={handleAddHomeScreenSubmit}
              prompt={prompt}
              installed={installed}
              promptToInstall={promptToInstall}
            />
          )}
      </div>
    </ThemeProvider>
  );
}

function LocalizedProviders({ children }: { children: React.ReactNode }) {
  const { language } = useTranslationsContext();
  const locale = language === Languages.DEUTSCH ? "de" : "en";
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
      {children}
    </LocalizationProvider>
  );
}

function AppWithGlobalProviders(): JSX.Element {
  return (
    <TranslationsProvider>
      <LocalizedProviders>
        <UserProvider>
          <AttendanceProvider>
            <SnackbarProvider
              dense
              autoHideDuration={4000}
              maxSnack={3}
              anchorOrigin={{ horizontal: "right", vertical: "top" }}
              action={(key) => (
                <IconButton
                  size="small"
                  onClick={() => closeSnackbar(key)}
                  sx={{ color: "rgba(224,221,240,0.6)", "&:hover": { color: "#e0ddf0" } }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            >
              <AppLayout />
            </SnackbarProvider>
          </AttendanceProvider>
        </UserProvider>
      </LocalizedProviders>
    </TranslationsProvider>
  );
}

const router = [
  {
    path: ROUTES.HOME,
    element: <AppWithGlobalProviders />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <Home />,
      },
      {
        path: ROUTES.CLASS_DETAIL,
        element: <ClassDetail />,
      },
      {
        path: ROUTES.CLASS_ATTENDANCE,
        element: <ClassAttendance />,
      },
      {
        path: ROUTES.STUDENT_ATTENDANCE,
        element: <StudentAttendance />,
      },
      {
        path: ROUTES.TRACKIFY,
        element: <Navigate to={ROUTES.HOME} replace />,
      },
      {
        path: ROUTES.TRACKIFY_CLASS,
        element: <TrackifyClassView />,
      },
      {
        path: ROUTES.CALENDFY,
        element: <CalendFyPage />,
      },
      {
        path: ROUTES.NOTIFY_SETTINGS,
        element: <NotifySettingsPage />,
      },
      {
        path: ROUTES.LOGIFY,
        element: <LogiFyPage />,
      },
      {
        path: ROUTES.REPORTIFY,
        element: <ReportifyPage />,
      },
      {
        path: ROUTES.REPORTIFY_DETAIL,
        element: <ReportDetailPage />,
      },
      {
        path: ROUTES.LOG_IN,
        element: <Login />,
      },
      {
        path: ROUTES.SIGN_UP,
        element: <SignUp />,
      },
      {
        path: ROUTES.FORGOT_PASSWORD,
        element: <ForgotPassword />,
      },
      {
        path: ROUTES.UPDATE_PASSWORD,
        element: <UpdatePassword />,
      },
      {
        path: ROUTES.DEMO,
        element: <DemoClassAttendance />,
      },
      {
        path: ROUTES.DEMO_STUDENT,
        element: <DemoStudentTimeline />,
      },
      {
        path: ROUTES.NOT_FOUND,
        element: <PageNotFound />,
      },
    ],
  },
];
export default router;
